import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, ChevronRight, Lightbulb, X, Sparkles } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Rate limiting constants
const SUGGESTION_LIMIT = 3;
const SUGGESTION_LIMIT_KEY = 'hsl_suggestion_limit';

interface ServiceItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  available: boolean;
}

interface SuggestionLimit {
  count: number;
  date: string;
}

export default function ServicesHub() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestionText, setSuggestionText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services: ServiceItem[] = [
    {
      id: 'fare-validator',
      title: 'Fare Validator',
      description: 'Get accurate travel fare estimates for tuk-tuks and taxis. Know the fair price before you ride.',
      icon: <img src="/tuk-tuk.png" alt="Tuk Tuk" className="w-10 h-10" />,
      path: '/app/services/fare-validator',
      available: true,
    },
  ];

  const handleServiceClick = (service: ServiceItem) => {
    if (service.available) {
      navigate(service.path);
    }
  };

  // Check rate limit from localStorage
  const checkRateLimit = (): boolean => {
    try {
      const stored = localStorage.getItem(SUGGESTION_LIMIT_KEY);
      if (!stored) return true;

      const limit: SuggestionLimit = JSON.parse(stored);
      const today = new Date().toDateString();

      // Reset if different day
      if (limit.date !== today) return true;

      return limit.count < SUGGESTION_LIMIT;
    } catch {
      return true;
    }
  };

  // Update rate limit in localStorage
  const updateRateLimit = () => {
    const today = new Date().toDateString();
    const stored = localStorage.getItem(SUGGESTION_LIMIT_KEY);
    
    let limit: SuggestionLimit = { count: 1, date: today };
    
    if (stored) {
      try {
        const parsed: SuggestionLimit = JSON.parse(stored);
        if (parsed.date === today) {
          limit.count = parsed.count + 1;
        }
      } catch {
        // Use default
      }
    }
    
    localStorage.setItem(SUGGESTION_LIMIT_KEY, JSON.stringify(limit));
  };

  const handleSuggestionSubmit = async () => {
    // Validation
    if (suggestionText.trim().length < 10) {
      showToast('Please enter at least 10 characters', 'error');
      return;
    }

    if (suggestionText.trim().length > 500) {
      showToast('Please keep your suggestion under 500 characters', 'error');
      return;
    }

    // Rate limit check
    if (!checkRateLimit()) {
      showToast('You\'ve reached the daily limit. Try again tomorrow!', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/suggestions/service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: suggestionText.trim(),
          type: 'service_request',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit suggestion');
      }

      // Update rate limit
      updateRateLimit();

      // Success
      showToast('Thanks! We\'ll consider your suggestion 💡', 'success');
      setSuggestionText('');
      setShowSuggestionModal(false);
    } catch (error) {
      showToast('Failed to submit. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const charCount = suggestionText.length;
  const isValidLength = charCount >= 10 && charCount <= 500;

  return (
    <div className="h-full w-full overflow-y-auto bg-gradient-to-br from-[#F5F5F5] via-[#F8F8F8] to-[#F2F2F2]">
      <div className="max-w-2xl mx-auto p-3 md:p-6">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8 pt-2 md:pt-4">
          <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl md:rounded-2xl shadow-lg mb-3 md:mb-4">
            <img src="/tuk-tuk.png" alt="Services" className="w-7 h-7 md:w-10 md:h-10" />
          </div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">
            Services
          </h1>
          <p className="text-gray-600 text-xs md:text-base">
            Tools to help you travel smarter in Sri Lanka
          </p>
        </div>

        {/* Services List */}
        <div className="space-y-3 md:space-y-4">
          {services.map((service) => (
            <button
              key={service.id}
              onClick={() => handleServiceClick(service)}
              disabled={!service.available}
              className={`w-full bg-white rounded-xl md:rounded-2xl p-3.5 md:p-5 shadow-md md:shadow-lg border transition-all duration-200 text-left ${
                service.available
                  ? 'border-[#D9D9D9] hover:border-[#BFBFBF] hover:shadow-xl cursor-pointer active:scale-[0.98]'
                  : 'border-[#D9D9D9] opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center gap-3 md:gap-4">
                {/* Icon */}
                <div className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl flex items-center justify-center ${
                  service.available 
                    ? 'bg-gradient-to-br from-amber-100 to-orange-100' 
                    : 'bg-gray-100'
                }`}>
                  <img src="/tuk-tuk.png" alt="Tuk Tuk" className="w-7 h-7 md:w-10 md:h-10" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 md:mb-1">
                    <h3 className="text-sm md:text-lg font-semibold text-gray-900">
                      {service.title}
                    </h3>
                    {!service.available && (
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] md:text-xs rounded-full">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 line-clamp-2">
                    {service.description}
                  </p>
                </div>

                {/* Arrow */}
                {service.available && (
                  <ChevronRight className="flex-shrink-0 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                )}
              </div>
            </button>
          ))}

          {/* Suggest a Service Card - Attention Grabber */}
          <button
            onClick={() => setShowSuggestionModal(true)}
            className="w-full rounded-xl md:rounded-2xl p-3.5 md:p-5 shadow-md md:shadow-lg border-2 border-dashed border-purple-300 
                       bg-gradient-to-br from-purple-50 via-indigo-50 to-pink-50
                       hover:border-purple-400 hover:shadow-xl 
                       transition-all duration-200 text-left cursor-pointer active:scale-[0.98]
                       relative overflow-hidden group"
          >
            {/* Animated sparkle effect */}
            <div className="absolute top-2 right-2 opacity-60 group-hover:opacity-100 transition-opacity">
              <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-purple-400 animate-pulse" />
            </div>
            
            <div className="flex items-center gap-3 md:gap-4">
              {/* Icon */}
              <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl flex items-center justify-center 
                              bg-gradient-to-br from-purple-200 to-indigo-200
                              group-hover:from-purple-300 group-hover:to-indigo-300 transition-colors">
                <Lightbulb className="w-6 h-6 md:w-8 md:h-8 text-purple-600" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 md:gap-2 mb-0.5 md:mb-1 flex-wrap">
                  <h3 className="text-sm md:text-lg font-semibold text-purple-900">
                    Suggest a Service
                  </h3>
                  <span className="px-1.5 md:px-2 py-0.5 bg-purple-100 text-purple-600 text-[10px] md:text-xs rounded-full font-medium">
                    We're listening!
                  </span>
                </div>
                <p className="text-xs md:text-sm text-purple-700/80">
                  What would help your Sri Lanka journey?
                </p>
              </div>

              {/* Arrow */}
              <ChevronRight className="flex-shrink-0 w-4 h-4 md:w-5 md:h-5 text-purple-400 group-hover:text-purple-600 transition-colors" />
            </div>
          </button>
        </div>

        {/* Info Card */}
        <div className="mt-6 md:mt-8 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-5 border border-[#D9D9D9]">
          <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm md:text-base">
            <Calculator className="w-4 h-4 md:w-5 md:h-5 text-[#BFBFBF]" />
            Why use our services?
          </h3>
          <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-[#BFBFBF] mt-0.5">•</span>
              <span>Get fair prices and avoid tourist traps</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#BFBFBF] mt-0.5">•</span>
              <span>Understand local transport pricing factors</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#BFBFBF] mt-0.5">•</span>
              <span>Travel confidently with accurate information</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Suggestion Modal */}
      {showSuggestionModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowSuggestionModal(false)}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-3 md:p-5 border-b border-gray-100">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-gradient-to-br from-purple-200 to-indigo-200 flex items-center justify-center">
                  <Lightbulb className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-sm md:text-lg font-semibold text-gray-900">Suggest a Service</h2>
                  <p className="text-[10px] md:text-xs text-gray-500">Help us build what you need</p>
                </div>
              </div>
              <button
                onClick={() => setShowSuggestionModal(false)}
                className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 md:w-5 md:h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-3 md:p-5">
              <textarea
                value={suggestionText}
                onChange={(e) => setSuggestionText(e.target.value)}
                placeholder="What service would help your Sri Lanka trip? E.g., 'Airport transfer', 'Tour guide finder'..."
                className="w-full h-24 md:h-32 p-3 md:p-4 border border-gray-200 rounded-lg md:rounded-xl resize-none 
                           focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
                           placeholder:text-gray-400 text-gray-800 text-sm md:text-base"
                maxLength={500}
              />
              
              {/* Character counter */}
              <div className="flex justify-between items-center mt-1.5 md:mt-2">
                <span className={`text-[10px] md:text-xs ${
                  charCount < 10 ? 'text-orange-500' : 
                  charCount > 500 ? 'text-red-500' : 'text-gray-400'
                }`}>
                  {charCount < 10 ? `${10 - charCount} more characters needed` : 
                   charCount > 500 ? 'Too long!' : `${charCount}/500`}
                </span>
                {isValidLength && (
                  <span className="text-[10px] md:text-xs text-green-500">✓ Ready</span>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-2 md:gap-3 p-3 md:p-5 border-t border-gray-100">
              <button
                onClick={() => setShowSuggestionModal(false)}
                className="flex-1 py-2.5 md:py-3 px-3 md:px-4 rounded-lg md:rounded-xl border border-gray-200 text-gray-700 font-medium text-sm md:text-base
                           hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSuggestionSubmit}
                disabled={!isValidLength || isSubmitting}
                className="flex-1 py-2.5 md:py-3 px-3 md:px-4 rounded-lg md:rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 
                           text-white font-medium text-sm md:text-base hover:from-purple-600 hover:to-indigo-600 
                           transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
